import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chatService } from "@/features/settings/services/chat.service";
import type { ChatMessage } from "@/features/settings/modal/chatSettings.types";

type UseChatReturn = {
  messages: ChatMessage[];
  textInputValue: string;
  imageUris: string[];
  setTextInputValue: (value: string) => void;
  removeImage: (index: number) => void;
  onSend: () => Promise<void>;
  handleAttachFiles: (files: FileList | null) => void;
  loading: boolean;
};

type RawAttachment = {
  file_url?: string;
};

type RawMessageUser = {
  _id?: string | number;
  name?: string;
  avatar?: string;
};

type RawMessageSender = {
  id?: string | number;
  full_name?: string;
  avatar?: string;
};

type RawChatMessage = {
  _id?: string | number;
  id?: string | number;
  message_id?: string | number;
  text?: string;
  message?: string;
  createdAt?: string | Date;
  created_at?: string | Date;
  user?: RawMessageUser;
  sender?: RawMessageSender;
  sender_id?: string | number;
  senderId?: string | number;
  sender_name?: string;
  image?: string;
  images?: string[];
  attachments?: RawAttachment[];
};

type ChatMessagesPayload = {
  items?: RawChatMessage[];
};

const toRawMessageArray = (value: unknown): RawChatMessage[] => {
  if (!Array.isArray(value)) return [];
  return value as RawChatMessage[];
};

const normalizeMessage = (raw: RawChatMessage): ChatMessage => {
  const attachmentImages = Array.isArray(raw?.attachments)
    ? raw.attachments
        .map((item) => item?.file_url)
        .filter((url: unknown): url is string => typeof url === "string" && !!url.trim())
    : [];

  return {
    _id: String(raw?._id ?? raw?.id ?? raw?.message_id ?? crypto.randomUUID()),
    text: raw?.text ?? raw?.message ?? "",
    createdAt: raw?.createdAt ?? raw?.created_at ?? new Date().toISOString(),
    user: {
      _id: String(raw?.user?._id ?? raw?.sender?.id ?? raw?.sender_id ?? raw?.senderId ?? "admin"),
      name: String(raw?.user?.name ?? raw?.sender?.full_name ?? raw?.sender_name ?? "Support"),
      avatar: raw?.user?.avatar ?? raw?.sender?.avatar,
    },
    images: Array.isArray(raw?.images)
      ? raw.images
      : Array.isArray(raw?.attachments)
        ? attachmentImages
        : raw?.image
          ? [raw.image]
          : [],
  };
};

export const useChat = (sessionId: string): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [textInputValue, setTextInputValue] = useState("");
  const [imageUris, setImageUris] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingRef = useRef(false);

  const fetchMessages = useCallback(async () => {
    if (!sessionId || isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    try {
      const response = await chatService.getMessages(sessionId);
      const payload = response as ChatMessagesPayload | RawChatMessage[] | undefined;

      const list = Array.isArray(payload)
        ? toRawMessageArray(payload)
        : toRawMessageArray(payload?.items);
      setMessages(Array.isArray(list) ? list.map(normalizeMessage) : []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    void fetchMessages();
    const id = setInterval(() => {
      void fetchMessages();
    }, 5000);
    intervalRef.current = id;
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [sessionId, fetchMessages]);

  const onSend = useCallback(async () => {
    const text = textInputValue.trim();
    if (!sessionId || (!text && imageUris.length === 0)) {
      return;
    }
    setLoading(true);
    const filesToSend = [...imageUris];
    setTextInputValue("");
    setImageUris([]);
    try {
      await chatService.sendMessage({
        conversation_id: sessionId,
        text,
        fileUris: filesToSend.length ? filesToSend : undefined,
      });
      await fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      setTextInputValue(text);
      setImageUris(filesToSend);
    } finally {
      setLoading(false);
    }
  }, [sessionId, textInputValue, imageUris, fetchMessages]);

  const removeImage = useCallback((index: number) => {
    setImageUris((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAttachFiles = useCallback((files: FileList | null) => {
    if (!files?.length) {
      return;
    }
    const newUris = Array.from(files).map((file) => URL.createObjectURL(file));
    setImageUris((prev) => [...prev, ...newUris]);
  }, []);

  return useMemo(
    () => ({
      messages,
      textInputValue,
      imageUris,
      setTextInputValue,
      removeImage,
      onSend,
      handleAttachFiles,
      loading,
    }),
    [messages, textInputValue, imageUris, removeImage, onSend, handleAttachFiles, loading]
  );
};
